<script lang="ts">
  // Forecast AI · Poster: a photo with the plan day as the headline and how the ride
  // measured up to today's target.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'
  import XssTargets from '../parts/XssTargets.svelte'

  export let ctx: ReportContext

  $: x = ctx.data.xfai!
  $: stats = statsFor(ctx, ['stat.time', 'stat.distance', 'stat.difficulty', 'stat.xep', 'stat.recovery'])
  $: ratio = x.target.xss > 0 ? x.completed.xss / x.target.xss : null
  $: verdict = x.day_kind === 'rest'
    ? { text: 'Recovery day', icon: 'check' }
    : ratio === null ? null
    : ratio >= 0.9 && ratio <= 1.2 ? { text: 'Target hit', icon: 'check' }
    : ratio > 1.2 ? { text: 'Above target', icon: 'trending' }
    : { text: 'Below target', icon: 'target' }
</script>

<Frame {ctx} shade="both" base="radial-gradient(120% 80% at 50% 0%, #2c2f78 0%, #0d0f2a 70%)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow"><Icon name="sparkles" size={32} stroke={2.2} color="var(--accent)" /> Forecast AI</span>
      <Brand height={40} />
    </div>

    {#if ctx.show('plan')}
      <div class="xr-plan">{x.plan_name}{x.focus ? ` · ${x.focus.name}` : ''}</div>
    {/if}

    {#if ctx.show('progress') && x.day && x.days_total}
      <div class="xr-progress">
        <div class="xr-day">
          <span class="xr-day-label">Day</span>
          <span class="xr-day-num">{x.day}</span>
        </div>
        <div class="xr-day-sub">of {x.days_total}{x.phase ? ` · ${x.phase} phase` : ''}</div>
        <div class="xr-plan-track"><div class="xr-plan-fill" style="width: {(x.day / x.days_total) * 100}%;" /></div>
      </div>
    {/if}

    <div class="xr-outcome">
      <div class="xr-result">
        {#if ctx.show('verdict') && verdict}
          <span class="xr-verdict"><Icon name={verdict.icon} size={30} stroke={3} color="#0b1114" /> {verdict.text}</span>
        {/if}
        <span class="xr-xss"><strong>{x.completed.xss}</strong> / {x.target.xss} XSS</span>
      </div>
      {#if ctx.show('targets')}
        <div class="xr-targets"><XssTargets {ctx} compact /></div>
      {/if}
    </div>

    <div class="xr-footer">
      <div class="xr-footer-title"><TitleBlock {ctx} lines={1} /></div>
      {#if stats.length}<div class="xr-footer-stats"><StatGrid {stats} columns={4} size="sm" /></div>{/if}
      <div class="xr-footer-badges"><Badges {ctx} own="xfai" /></div>
    </div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' 'plan' '.' 'progress' 'outcome' 'footer';
  }
  /* Landscape: the plan day bottom left, how the ride measured up beside it. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: minmax(0, 1fr) 640px;
    grid-template-rows: auto auto minmax(0, 1fr) auto auto;
    grid-template-areas: 'top top' 'plan plan' '. .' 'progress outcome' 'footer footer';
    column-gap: 80px;
  }
  .xr-top { grid-area: top; }
  .xr-plan { grid-area: plan; }
  .xr-progress { grid-area: progress; min-width: 0; }
  .xr-outcome { grid-area: outcome; min-width: 0; }
  :global(.xr-landscape) .xr-outcome { align-self: end; }
  .xr-footer { grid-area: footer; }
  .xr-footer-badges:empty { display: none; }
  :global(.xr-square) .xr-day-label { font-size: 84px; }
  :global(.xr-square) .xr-day-num { font-size: 210px; }
  :global(.xr-square) .xr-day-sub { font-size: 44px; margin-top: 8px; }
  :global(.xr-square) .xr-plan-track { margin-top: 16px; }
  :global(.xr-square) .xr-result { margin-top: 24px; }
  :global(.xr-square) .xr-xss strong { font-size: 66px; }
  :global(.xr-square) .xr-targets { margin-top: 18px; }
  :global(.xr-square) .xr-footer { margin-top: 24px; padding-top: 22px; gap: 18px; }
  :global(.xr-landscape) .xr-day-num { font-size: 270px; }
  :global(.xr-landscape) .xr-result { margin-top: 0; }
  :global(.xr-landscape) .xr-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
    grid-template-areas: 'title stats' 'badges badges';
    column-gap: 60px;
    row-gap: 22px;
    align-items: end;
  }
  :global(.xr-landscape) .xr-footer-title { grid-area: title; min-width: 0; }
  :global(.xr-landscape) .xr-footer-stats { grid-area: stats; }
  :global(.xr-landscape) .xr-footer-badges { grid-area: badges; }
  .xr-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .xr-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: 0.26em;
    text-transform: uppercase;
  }
  .xr-plan {
    margin-top: 18px;
    font-size: 30px;
    font-weight: 600;
    color: var(--muted);
  }
  .xr-day {
    display: flex;
    align-items: baseline;
    gap: 20px;
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    text-transform: uppercase;
    line-height: 0.8;
    text-shadow: 0 4px 30px rgba(0, 0, 0, 0.4);
  }
  .xr-day-label {
    font-size: 110px;
    color: var(--accent);
  }
  .xr-day-num {
    font-size: 290px;
  }
  .xr-day-sub {
    margin-top: 12px;
    font-family: var(--display);
    font-size: 54px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .xr-plan-track {
    margin-top: 22px;
    height: 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.2);
  }
  .xr-plan-fill {
    height: 100%;
    border-radius: 999px;
    background: var(--accent);
  }
  .xr-result {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 34px;
  }
  .xr-verdict {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 26px 12px 18px;
    border-radius: 999px;
    background: var(--accent);
    color: #0b1114;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .xr-xss {
    margin-left: auto;
    font-family: var(--display);
    font-size: 46px;
    font-weight: 600;
    color: var(--muted);
  }
  .xr-xss strong {
    font-size: 80px;
    font-weight: 800;
    color: #fff;
  }
  .xr-targets {
    margin-top: 24px;
  }
  .xr-footer {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 34px;
    padding-top: 28px;
    border-top: 2px solid var(--faint);
  }
</style>
